import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Flex } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { serialize } from 'object-to-formdata';
import HeaderNavigation from 'components/HeaderNavigation';
import { FORM_MAX_WIDTH } from 'utils/constants';
import FooterButtons, { FOOTER_HEIGHT } from 'components/FooterButtons';
import FormBody from 'pages/OnboardScreens/Form/components/FormBody';
import { BODY_SCHEMA, DEFAULT_VALUES } from 'pages/OnboardScreens/Form/constants';
import ROUTES from 'app/routes';
import { API, APIRoutes } from 'api';
import { setFormErrors } from 'utils/api';
import LoadingIndicator from 'components/LoadingIndicator';
import ComponentPreview from 'components/PreviewWrapper';
import { SERIALIZER_OPTIONS } from 'app/constants';
import { useGetOnboardScreen } from 'api/onboardScreens';
import FormWrapper from 'components/FormWrapper';

function OnboardScreenEdit() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const methods = useForm<OnboardScreen>({
    mode: 'onChange',
    resolver: yupResolver(BODY_SCHEMA),
    defaultValues: DEFAULT_VALUES,
  });

  const { handleSubmit, setError, reset } = methods;

  const { data: { data: onboardScreen } = {}, isFetching } = useGetOnboardScreen(+id);

  const { mutate: editOnboardScreen, isLoading: isEditing } = useMutation({
    mutationKey: 'onboardScreensMutation',
    mutationFn: (data: OnboardScreen) => {
      const formData = serialize(data, SERIALIZER_OPTIONS, new FormData(), 'onboard_screen');
      return API.put(APIRoutes.onboardScreens.byId(+id), formData);
    },
    onSuccess: () => {
      toast.success('Zmiany w ekranie zostały zastosowane');
      navigate(ROUTES.onboardScreens.base);
      void queryClient.invalidateQueries('onboardScreens');
    },
    onError: ({ errors }) => {
      setFormErrors(setError, errors);
      toast.error('Wystąpił problem podczas edycji ekranu');
    },
  });

  const onSubmit = handleSubmit((data: OnboardScreen) => editOnboardScreen(data));

  useEffect(() => {
    if (onboardScreen) reset({ ...DEFAULT_VALUES, ...onboardScreen });
  }, [onboardScreen, reset]);

  const isLoading = isFetching || isEditing;

  return (
    <Box>
      <HeaderNavigation
        baseCrumb={{
          label: 'On-boarding',
          to: ROUTES.onboardScreens.base,
        }}
        crumbs={[{ to: '', label: 'Edytuj ekran' }]}
      />
      {isFetching && <LoadingIndicator />}
      <FormWrapper {...methods}>
        <Flex gap={4} mb={4} alignItems="flex-start" pb={FOOTER_HEIGHT}>
          <Box w="100%" as="form" id="onboard-screen-editor" onSubmit={onSubmit} maxW={FORM_MAX_WIDTH}>
            <FormBody isLoading={isLoading} />
          </Box>
          <ComponentPreview kind="onboard_screen" isEditMode />
        </Flex>
      </FormWrapper>
      <FooterButtons isLoading={isLoading} formId="onboard-screen-editor" onCancel={() => navigate(-2)} />
    </Box>
  );
}

export default OnboardScreenEdit;
